import type { ShopperOrders } from '@/scapi';
import type { TFunction } from 'i18next';

/**
 * Order and shipping status labels and badge styles for account order UI.
 *
 * External order statuses come from the backend as UPPERCASE tokens — match them
 * exactly; do not normalize case/separators on the frontend.
 */

/** SCAPI Order.status enum type. */
export type OrderStatusType = NonNullable<ShopperOrders.schemas['Order']['status']>;

/** SCAPI Order.shippingStatus / Shipment.shippingStatus enum type (Order has all three; Shipment has not_shipped | shipped). */
export type ShippingStatusType = NonNullable<ShopperOrders.schemas['Order']['shippingStatus']>;

export type OrderStatusLabelKey =
    | 'orders.status.created'
    | 'orders.status.new'
    | 'orders.status.completed'
    | 'orders.status.cancelled'
    | 'orders.status.replaced'
    | 'orders.status.failed';

export type CustomOrderStatusLabelKey =
    | 'orders.customStatus.inProgress'
    | 'orders.customStatus.shipped'
    | 'orders.customStatus.completed'
    | 'orders.customStatus.cancelled'
    | 'orders.customStatus.partiallyShipped'
    | 'orders.customStatus.partiallyCompleted';

export type OrderStatusBadgeSchema = 'default' | 'neutral' | 'positive' | 'warning' | 'danger';

export type OrderStatusBadgeHierarchy = 'primary' | 'secondary';

export type OrderStatusBadgeIcon = 'x' | 'check';

export interface OrderStatusConfig {
    labelKey: OrderStatusLabelKey;
    badgeSchema: OrderStatusBadgeSchema;
    badgeHierarchy?: OrderStatusBadgeHierarchy;
}

export interface CustomOrderStatusConfig {
    labelKey: CustomOrderStatusLabelKey;
    badgeSchema: OrderStatusBadgeSchema;
    badgeHierarchy?: OrderStatusBadgeHierarchy;
}

/** Known Eataly external order status tokens (backend source of truth — UPPERCASE). */
export const EXTERNAL_ORDER_STATUSES = [
    'IN_PROGRESS',
    'SHIPPED',
    'COMPLETED',
    'CANCELLED',
    'PARTIALLY_SHIPPED',
    'PARTIALLY_COMPLETED',
] as const;

export const ORDER_STATUS_TYPES = ['created', 'new', 'completed', 'cancelled', 'replaced', 'failed'] as const;

export type ExternalOrderStatusType = (typeof EXTERNAL_ORDER_STATUSES)[number];

const STATUS_CONFIG: Record<OrderStatusType, OrderStatusConfig> = {
    created: {
        labelKey: 'orders.status.created',
        badgeSchema: 'default',
    },
    new: {
        labelKey: 'orders.status.new',
        badgeSchema: 'default',
    },
    completed: {
        labelKey: 'orders.status.completed',
        badgeSchema: 'positive',
    },
    cancelled: {
        labelKey: 'orders.status.cancelled',
        badgeSchema: 'danger',
    },
    replaced: {
        labelKey: 'orders.status.replaced',
        badgeSchema: 'positive',
    },
    failed: {
        labelKey: 'orders.status.failed',
        badgeSchema: 'danger',
    },
};

/** Eataly order list badges (Figma Drop 3 — node 23:21799). */
const CUSTOM_ORDER_STATUS_CONFIG: Record<ExternalOrderStatusType, CustomOrderStatusConfig> = {
    IN_PROGRESS: {
        labelKey: 'orders.customStatus.inProgress',
        badgeSchema: 'warning',
    },
    SHIPPED: {
        labelKey: 'orders.customStatus.shipped',
        badgeSchema: 'positive',
    },
    COMPLETED: {
        labelKey: 'orders.customStatus.completed',
        badgeSchema: 'positive',
    },
    CANCELLED: {
        labelKey: 'orders.customStatus.cancelled',
        badgeSchema: 'danger',
    },
    PARTIALLY_SHIPPED: {
        labelKey: 'orders.customStatus.partiallyShipped',
        badgeSchema: 'warning',
    },
    PARTIALLY_COMPLETED: {
        labelKey: 'orders.customStatus.partiallyCompleted',
        badgeSchema: 'default',
        badgeHierarchy: 'secondary',
    },
};

export type ShippingStatusLabelKey =
    | 'orders.shippingStatus.notShipped'
    | 'orders.shippingStatus.partShipped'
    | 'orders.shippingStatus.shipped';

export interface ShippingStatusConfig {
    labelKey: ShippingStatusLabelKey;
    className: string;
}

const SHIPPING_STATUS_CONFIG: Record<ShippingStatusType, ShippingStatusConfig> = {
    not_shipped: {
        labelKey: 'orders.shippingStatus.notShipped',
        className: 'border-transparent bg-info text-info-foreground',
    },
    part_shipped: {
        labelKey: 'orders.shippingStatus.partShipped',
        className: 'border-transparent bg-info text-info-foreground',
    },
    shipped: {
        labelKey: 'orders.shippingStatus.shipped',
        className: 'border-transparent bg-status-positive text-white',
    },
};

export function isExternalOrderStatus(status: string | undefined | null): status is ExternalOrderStatusType {
    return status != null && (EXTERNAL_ORDER_STATUSES as readonly string[]).includes(status);
}

function isOrderStatusType(status: string | undefined | null): status is OrderStatusType {
    return status != null && (ORDER_STATUS_TYPES as readonly string[]).includes(status);
}

type OrderWithStatuses = {
    externalOrderStatus?: string | null;
    status?: string | null;
};

/**
 * Failed SCAPI orders always display as failed. Otherwise, prefers a known external
 * order status and falls back to SCAPI `status`.
 * Unknown / empty external values are ignored (backend tokens must match exactly).
 */
export function getOrderDisplayStatus(
    order: OrderWithStatuses
): ExternalOrderStatusType | OrderStatusType | string | undefined {
    if (order.status === 'failed') {
        return 'failed';
    }
    if (isExternalOrderStatus(order.externalOrderStatus)) {
        return order.externalOrderStatus;
    }
    return order.status?.trim() || undefined;
}

/**
 * Formats raw fallback status text for display:
 * - `not_shipped` -> `Not Shipped`
 * - `SHIPPED` -> `Shipped`
 */
export function formatStatusFallbackLabel(status: string | undefined): string {
    if (status == null || status.trim() === '') {
        return '';
    }
    return status
        .trim()
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .split(/\s+/)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
        .join(' ');
}

/**
 * Colored-badge config for SCAPI `Order.status` only. Unknown / missing / empty → `undefined`.
 */
export function getOrderStatusConfig(status: string | undefined): OrderStatusConfig | undefined {
    if (!isOrderStatusType(status)) {
        return undefined;
    }
    return STATUS_CONFIG[status];
}

/**
 * Badge config for known Eataly external order statuses. Exact match only.
 */
export function getCustomOrderStatusConfig(status: string | undefined): CustomOrderStatusConfig | undefined {
    if (!isExternalOrderStatus(status)) {
        return undefined;
    }
    return CUSTOM_ORDER_STATUS_CONFIG[status];
}

export function getOrderStatusConfigGeneral(
    status: string | undefined
): OrderStatusConfig | CustomOrderStatusConfig | undefined {
    if (isExternalOrderStatus(status)) {
        return getCustomOrderStatusConfig(status);
    }
    return getOrderStatusConfig(status);
}
/**
 * Resolves the human-readable status label for aria text / captions (not badge rendering).
 * Prefer {@link OrderStatusBadge} for visual badges.
 */
export function getOrderStatusDisplayLabel(
    status: string | undefined,
    t: TFunction<'account', undefined>,
    overrideLabel?: string
): string {
    const override = overrideLabel?.trim();
    if (override) {
        return override;
    }
    const customConfig = getCustomOrderStatusConfig(status);
    if (customConfig) {
        return t(customConfig.labelKey);
    }
    const orderConfig = getOrderStatusConfig(status);
    if (orderConfig) {
        return t(orderConfig.labelKey);
    }
    return formatStatusFallbackLabel(status);
}

/**
 * Colored-badge config for SCAPI shipment/order shipping status only. Unknown / missing / empty → `undefined`.
 */
export function getShippingStatusConfig(status: string | undefined): ShippingStatusConfig | undefined {
    if (status == null || status.trim() === '') {
        return undefined;
    }
    return status in SHIPPING_STATUS_CONFIG ? SHIPPING_STATUS_CONFIG[status as ShippingStatusType] : undefined;
}
